import type { IDisposable, ITheme, Terminal } from '@xterm/xterm';

/**
 * Light/dark appearance for the terminals that follow Eddy's theme (Pro
 * Builder, FRONT-3003). Pure — no DOM, no xterm instance — so the palettes and
 * the colour-scheme notification protocol are testable under plain node. The
 * hook only wires them (`use-pty-terminal.ts`, option `appearance`).
 */

export type TerminalMode = 'light' | 'dark';

/** xterm's `minimumContrastRatio` for a following terminal: a program that picked colours for the other scheme stays readable. */
export const FOLLOW_APP_MIN_CONTRAST = 4.5;

/**
 * Panel colour used only when resolving the real `--card` token fails (no
 * canvas, unparsable value). Never the primary source — a literal here can
 * drift from the token, which is why the hook resolves it live.
 */
export const FALLBACK_BACKGROUND: Record<TerminalMode, string> = {
  dark: '#18181b',
  light: '#ffffff',
};

interface ModePalette {
  foreground: string;
  cursor: string;
  selectionBackground: string;
  /** ANSI colours 0-15: normal black..white, then bright black..white. */
  ansi: readonly [
    string, string, string, string, string, string, string, string,
    string, string, string, string, string, string, string, string,
  ];
}

export const PALETTES: Record<TerminalMode, ModePalette> = {
  // Dark: the long-standing Pro Builder foreground family on a dark panel,
  // with light-leaning ANSI colours.
  dark: {
    foreground: '#e4e4e7',
    cursor: '#e4e4e7',
    selectionBackground: 'rgba(228, 228, 231, 0.25)',
    ansi: [
      '#71717a', '#f87171', '#4ade80', '#facc15', '#60a5fa', '#c084fc', '#22d3ee', '#d4d4d8',
      '#a1a1aa', '#fca5a5', '#86efac', '#fde047', '#93c5fd', '#d8b4fe', '#67e8f9', '#fafafa',
    ],
  },
  // Light: dark foreground and dark, saturated ANSI colours. Every hue (bright
  // included) clears 4.5:1 on white AND on a slightly tinted panel, so "bright"
  // is the lighter of two dark shades rather than a pastel. "White" (7) is a
  // mid-dark grey on purpose — a literal white would vanish on this panel.
  light: {
    foreground: '#18181b',
    cursor: '#18181b',
    selectionBackground: 'rgba(24, 24, 27, 0.18)',
    ansi: [
      '#18181b', '#991b1b', '#14532d', '#713f12', '#1e40af', '#6b21a8', '#155e75', '#52525b',
      '#71717a', '#b91c1c', '#166534', '#854d0e', '#1d4ed8', '#7e22ce', '#0e7490', '#3f3f46',
    ],
  },
};

/** The xterm theme for `mode` on the resolved opaque panel colour `background`. */
export function buildTerminalTheme(mode: TerminalMode, background: string): ITheme {
  const p = PALETTES[mode];
  return {
    background,
    foreground: p.foreground,
    cursor: p.cursor,
    cursorAccent: background,
    selectionBackground: p.selectionBackground,
    black: p.ansi[0], red: p.ansi[1], green: p.ansi[2], yellow: p.ansi[3],
    blue: p.ansi[4], magenta: p.ansi[5], cyan: p.ansi[6], white: p.ansi[7],
    brightBlack: p.ansi[8], brightRed: p.ansi[9], brightGreen: p.ansi[10], brightYellow: p.ansi[11],
    brightBlue: p.ansi[12], brightMagenta: p.ansi[13], brightCyan: p.ansi[14], brightWhite: p.ansi[15],
  };
}

/** `#rrggbb` from 0-255 channels. */
export function rgbToHex(r: number, g: number, b: number): string {
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

function luminance(hex: string): number {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) throw new Error(`not a #rrggbb colour: ${hex}`);
  const n = parseInt(m[1], 16);
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
}

/** WCAG 2 contrast ratio between two `#rrggbb` colours (1..21). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

// ── Colour-scheme notification (DEC private mode 2031 / DSR 996) ──────────
// The contour/ghostty protocol Claude Code uses to resolve its `auto` theme:
// with mode 2031 set the terminal reports the scheme whenever it changes, and
// `CSI ? 996 n` asks for it on demand. Reply: `CSI ? 997 ; 1 n` dark,
// `CSI ? 997 ; 2 n` light.
//
// Known residual, accepted: the mode is cleared on a terminal reset (RIS,
// DECSTR) or when the session ends, never on a program merely exiting. If
// Claude Code crashes without resetting 2031 and the shell carries on at its
// prompt, the next theme flip writes a scheme report to that prompt (a few
// visible characters, harmless). Telling "the program that set the mode
// exited" from "it is still running" would need process tracking the PTY
// stream does not give us.

export const THEME_NOTIFY_MODE = 2031;
export const SCHEME_QUERY_DSR = 996;

/** The scheme report bytes for `mode`. */
export function schemeReport(mode: TerminalMode): string {
  return `\x1b[?997;${mode === 'dark' ? 1 : 2}n`;
}

/** xterm's parser params: numbers, or arrays for `:`-separated sub-parameters. */
export type ParserParams = ReadonlyArray<number | number[]>;

function flatParams(params: ParserParams): number[] {
  return params.flatMap((p) => (Array.isArray(p) ? p : [p]));
}

export interface SchemeProtocol {
  /** Whether the program has set mode 2031 and not reset it since. */
  readonly enabled: boolean;
  /** A `CSI ? … h` (`set` true) or `CSI ? … l` (false) arrived. Only records 2031. */
  onPrivateMode(params: ParserParams, set: boolean): void;
  /** A `CSI ? … n` arrived. Returns true only when it was the 996 query (and answers it). */
  onDsr(params: ParserParams): boolean;
  /**
   * The terminal was reset (RIS or DECSTR) or the session ended: mode 2031 is
   * off again, so a theme flip no longer writes to whatever is at the prompt.
   */
  reset(): void;
  /** The app theme changed; reports to the program when 2031 is set and the mode really changed. */
  setMode(mode: TerminalMode): void;
}

/**
 * The 2031 state machine. `send` writes bytes to the program's input — in the
 * hook, the same ordered queue xterm's own replies use. The 996 query is
 * answered whether or not 2031 is set: the query is an on-demand read, the
 * mode only controls the unsolicited updates.
 */
export function createSchemeProtocol(send: (bytes: string) => void, initial: TerminalMode): SchemeProtocol {
  let mode = initial;
  let enabled = false;
  return {
    get enabled() {
      return enabled;
    },
    onPrivateMode(params, set) {
      if (!flatParams(params).includes(THEME_NOTIFY_MODE)) return;
      const was = enabled;
      enabled = set;
      // Enabling reports once straight away, so the program need not also query.
      if (set && !was) send(schemeReport(mode));
    },
    reset() {
      enabled = false;
    },
    onDsr(params) {
      const flat = flatParams(params);
      if (flat.length !== 1 || flat[0] !== SCHEME_QUERY_DSR) return false;
      send(schemeReport(mode));
      return true;
    },
    setMode(next) {
      if (next === mode) return;
      mode = next;
      if (enabled) send(schemeReport(mode));
    },
  };
}

/**
 * Hook `protocol` into xterm's parser. The `h`/`l` handlers ALWAYS return
 * false: those finals are DECSET/DECRST for every private mode (1049 alternate
 * screen, 2004 bracketed paste, mouse, cursor…) and may carry several in one
 * sequence, so xterm's own handling must still run; 2031 is only observed.
 * `n` returns true only for the 996 query, so xterm still answers its own DSRs.
 */
export function installSchemeProtocol(
  term: Pick<Terminal, 'parser'>,
  protocol: SchemeProtocol,
): IDisposable {
  const handlers = [
    term.parser.registerCsiHandler({ prefix: '?', final: 'h' }, (params) => {
      protocol.onPrivateMode(params, true);
      return false;
    }),
    term.parser.registerCsiHandler({ prefix: '?', final: 'l' }, (params) => {
      protocol.onPrivateMode(params, false);
      return false;
    }),
    term.parser.registerCsiHandler({ prefix: '?', final: 'n' }, (params) => protocol.onDsr(params)),
    // A full reset (ESC c) or soft reset (CSI ! p) switches every private mode
    // off. Observed only (false): xterm's own reset must still run.
    term.parser.registerEscHandler({ final: 'c' }, () => {
      protocol.reset();
      return false;
    }),
    term.parser.registerCsiHandler({ intermediates: '!', final: 'p' }, () => {
      protocol.reset();
      return false;
    }),
  ];
  return { dispose: () => handlers.forEach((h) => h.dispose()) };
}

/** The web font Eddy's `--font-mono` leads with (loaded by `@fontsource/ibm-plex-mono`, `main.tsx`). */
export const FOLLOW_APP_FONT_FAMILY = 'IBM Plex Mono';
export const FOLLOW_APP_FONT_SIZE = 13;
export const FOLLOW_APP_LINE_HEIGHT = 1.2;
/** What the loader is asked for: the family at the terminal's size. */
export const FOLLOW_APP_FONT_LOAD_SPEC = `${FOLLOW_APP_FONT_SIZE}px "${FOLLOW_APP_FONT_FAMILY}"`;

/** The slice of `document.fonts` (a `FontFaceSet`) the web-font watch uses; a fake in tests. */
export interface FontFaceSetLike {
  check(font: string): boolean;
  load(font: string): Promise<unknown>;
  addEventListener(type: 'loadingdone', listener: () => void): void;
  removeEventListener(type: 'loadingdone', listener: () => void): void;
}

/**
 * Calls `onReady` once, at the moment the web font is really usable
 * (`fonts.check(spec)` passes) — never on a timer. xterm measures its cell grid
 * from the face it is given, so naming a face that has not loaded yet leaves
 * the grid on the fallback's metrics after the real face arrives. The load
 * request settling is one chance to check; a font that arrives later by any
 * other path (another element using it, a retried request) fires
 * `loadingdone`, which is the next. A font that never arrives never calls
 * `onReady`, and the terminal simply stays on the fallback stack. Returns the
 * stop function (the terminal's teardown).
 */
export function whenWebFontReady(fonts: FontFaceSetLike, spec: string, onReady: () => void): () => void {
  let done = false;
  const stop = () => {
    done = true;
    fonts.removeEventListener('loadingdone', tryReady);
  };
  function tryReady() {
    if (done || !fonts.check(spec)) return;
    stop();
    onReady();
  }
  fonts.addEventListener('loadingdone', tryReady);
  void fonts.load(spec).then(tryReady, tryReady);
  return stop;
}

/** The stack with the web font's own entry dropped (the face to open on until it has loaded). */
export function stackWithoutWebFont(stack: string): string {
  return stack
    .split(',')
    .map((f) => f.trim())
    .filter((f) => f.replace(/^['"]|['"]$/g, '') !== FOLLOW_APP_FONT_FAMILY)
    .join(', ');
}

/** Font options for a terminal that follows Eddy (`appearance: 'follow-app'`); 'fixed' terminals never call this. */
export function followAppFontOptions(
  stack: string,
  webFontReady: boolean,
): { fontFamily: string; fontSize: number; lineHeight: number } {
  return {
    fontFamily: webFontReady ? stack : stackWithoutWebFont(stack),
    fontSize: FOLLOW_APP_FONT_SIZE,
    lineHeight: FOLLOW_APP_LINE_HEIGHT,
  };
}
